/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';

import { makeStyles, Theme } from '@material-ui/core/styles';

import MuiSelect, { SelectProps as MuiSelectProps } from '@material-ui/core/Select';

import MenuItem from '@material-ui/core/MenuItem';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import { FieldRenderProps } from 'react-final-form';


import LookupContext from '../../../context/LookupContext';
import { ILookup } from '@ngt/online-patient-management-ui';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


type SelectProps = Partial<MuiSelectProps> & FieldRenderProps<any, any>

interface ISelect extends SelectProps {
    nullOption?: boolean;
    lookup?: ILookup;
}

interface ISelectIconProps {
    className?: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


const useIconStyles = makeStyles<Theme>(theme => ({
    icon: {
        top: 'calc(50% - 8px)'
    }
}));


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

export const SelectIcon: React.FunctionComponent<ISelectIconProps> = ({ className }) => {
    const classes = useIconStyles();

    return (
        <FontAwesomeIcon icon={faChevronDown} className={`${className} ${classes.icon}`} fixedWidth />
    );
};

const Select: React.FunctionComponent<ISelect> = ({
    input: { name, value, onChange: onInputChange, checked, onBlur, onFocus, multiple, type },
    meta,
    onChange,
    children,
    fullWidth,
    nullOption,
    lookup,
    ...rest
}) => {
    const lookupContext = React.useContext(LookupContext);

    const onChangeCombined = React.useCallback((event: React.ChangeEvent<{ name: string | undefined; value: unknown; }>, child: React.ReactNode) => {
        if (onChange) {
            onChange(event, child);
        }

        onInputChange(event);
    }, [onChange, onInputChange]);

    return (
        <MuiSelect
            {...rest as any}
            inputProps={{
                onFocus,
                onBlur
            }}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            name={name}
            value={value}
            IconComponent={SelectIcon}
            onChange={onChangeCombined}
        >
            {
                (nullOption === undefined || nullOption) && (
                    <MenuItem
                        value={undefined}
                    >
                        &nbsp;
                    </MenuItem>
                )
            }
            {
                children ?? (lookup ?? lookupContext)?.items?.map(item => {
                    return (
                        <MenuItem
                            key={item.id}
                            value={item.id}
                        >
                            {item.value}
                        </MenuItem>
                    );
                })
            }
        </MuiSelect>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Select;
